// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * The battery of the machine an ML training runner is on, as its worker's heartbeat reports it.
 *
 * The runner holds its job — stopped, not failed — once the battery falls to `pauseAtPercent`,
 * and lets it carry on when it has charged back to `resumeAtPercent`. A held run looks exactly
 * like a hung one, which is why both the batch page and the ML page show this.
 */
export type MlBattery = {
  percent: number;
  paused: boolean;
  pauseAtPercent: number;
  resumeAtPercent: number;
};

/**
 * Maps the `mlBattery` / `workerBattery` object. Null for a machine without a battery, a
 * runner or geodata too old to report one, or anything without a usable percent.
 */
export const mapMlBattery = (value: unknown): MlBattery | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const pick = (...keys: string[]) => keys.map((k) => row[k]).find((v) => v !== undefined);
  const percent = pick('percent', 'Percent');
  const pauseAt = pick('pauseAtPercent', 'PauseAtPercent');
  const resumeAt = pick('resumeAtPercent', 'ResumeAtPercent');
  if (typeof percent !== 'number' || !Number.isFinite(percent)) return null;
  return {
    percent,
    paused: pick('paused', 'Paused') === true,
    pauseAtPercent: typeof pauseAt === 'number' ? pauseAt : 0,
    resumeAtPercent: typeof resumeAt === 'number' ? resumeAt : 0,
  };
};
